import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IPerDiemModalData } from '../../time-sheets/time-sheets.type';
import { TimeSheetsStoreService } from '../../time-sheets/time-sheets.store';
import { HttpErrorResponse } from '@angular/common/http';
import * as moment from 'moment';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

/**
 * dialog for pilotpay
 */
@Component({
	selector: 'app-pilotpay-diemdialog',
	styleUrls: ['./diemdialog.component.scss'],
	templateUrl: './diemdialog.component.html'
})
export class DiemDialogComponent implements OnInit {
	constructor(
		public dialogRef: MatDialogRef<DiemDialogComponent>,
		@Inject(MAT_DIALOG_DATA)
		public modalData: IPerDiemModalData,
		private fb: FormBuilder,
		private timeSheetsStoreService: TimeSheetsStoreService
	) {}

	public form: FormGroup;
	public isSubmitted: boolean;
	public errorMessage: string;
	public minDateValue: moment.Moment;
	public maxDateValue: moment.Moment;
	public startWithinTrip: boolean = false;
	public endWithinTrip: boolean = false;
	public withinTripWarningMessage: string = 'This Per Diem is in the middle of a trip';
	public startTimeHoursError: any;
	public startTimeMinutesError: any;
	public endTimeHoursError: any;
	public endTimeMinutesError: any;

	public ngOnInit(): void {
		this.form = this.fb.group({
			perDiemStart: [
				this.modalData.perDiem ? moment.utc(this.modalData.perDiem.perDiemStart) : null,
				Validators.required
			],
			perDiemEnd: [
				this.modalData.perDiem ? moment.utc(this.modalData.perDiem.perDiemEnd) : null,
				Validators.required
			],
			comments: [this.modalData.perDiem ? this.modalData.perDiem.comments : null],
			timeSheetPerDiemId: [this.modalData.perDiem ? this.modalData.perDiem.timeSheetPerDiemId : null],
			timeSheetId: [this.modalData.timesheet ? this.modalData.timesheet.timeSheetId : null]
		});

		this.minDateValue = moment.utc(this.modalData.timesheet.startDate);
		this.maxDateValue = moment.utc(this.modalData.timesheet.endDate);

		this.form.addControl(
			'startTime',
			new FormControl(this.form.value.perDiemStart ? this.getTimeValue(this.form.value.perDiemStart) : '00:00', [
				Validators.required
			])
		);

		this.form.addControl(
			'endTime',
			new FormControl(this.form.value.perDiemEnd ? this.getTimeValue(this.form.value.perDiemEnd) : '00:00', [
				Validators.required
			])
		);

		this.form
			.get('startTime')
			.valueChanges.pipe(
				debounceTime(300),
				distinctUntilChanged()
			)
			.subscribe(res => {
				if (res) {
					this.startTimeHoursError = res.length !== 5;
					this.startTimeMinutesError = res.length !== 5;
				}
			});

		this.form
			.get('endTime')
			.valueChanges.pipe(
				debounceTime(300),
				distinctUntilChanged()
			)
			.subscribe(res => {
				if (res) {
					this.endTimeHoursError = res.length !== 5;
					this.endTimeMinutesError = res.length !== 5;
				}
			});

		if (
			(this.modalData.timeSheetStatusError && !this.modalData.timeSheetLockOutError) ||
			this.modalData.timeSheetLockOutError
		) {
			this.form.disable();
		}
	}

	public getTimeValue(dateTime: moment.Moment): string {
		return dateTime ? dateTime.format('HH:mm') : null;
	}

	public save(): void {
		this.combineDateTimeValues('perDiemStart', 'startTime');
		this.combineDateTimeValues('perDiemEnd', 'endTime');

		this.isSubmitted = true;
		if (
			this.form.invalid ||
			this.startTimeHoursError ||
			this.startTimeMinutesError ||
			this.endTimeHoursError ||
			this.endTimeMinutesError ||
			!this.form.value.startTime ||
			!this.form.value.endTime
		) {
			return;
		}

		this.errorMessage = this.form.value.perDiemStart.isAfter(this.form.value.perDiemEnd)
			? 'Per-diem start date can not be after per-diem end date'
			: null;

		if (!this.form.invalid && !this.errorMessage) {
			this.form.get('perDiemStart').patchValue(this.form.value.perDiemStart.format('YYYY-MM-DDTHH:mm:ss'));
			this.form.get('perDiemEnd').patchValue(this.form.value.perDiemEnd.format('YYYY-MM-DDTHH:mm:ss'));

			if (this.form.value.timeSheetPerDiemId) {
				this.updatePerDiem();
			} else {
				this.addPerDiem();
			}
		}
	}

	public close(): void {
		this.dialogRef.close();
	}

	public addPerDiem(): void {
		this.timeSheetsStoreService.addPerDiem(this.form.value).subscribe(
			(response: any) => {
				this.dialogRef.close(response);
			},
			(error: HttpErrorResponse) => {
				this.errorMessage = error.error;
			}
		);
	}

	public updatePerDiem(): void {
		this.timeSheetsStoreService.updatePerDiem(this.form.value).subscribe(
			(response: any) => {
				this.dialogRef.close(response);
			},
			(error: HttpErrorResponse) => {
				this.errorMessage = error.error;
			}
		);
	}

	public deletePerDiem(): void {
		this.timeSheetsStoreService.deletePerDiem(this.modalData.perDiem.timeSheetPerDiemId).subscribe(
			(response: any) => {
				this.dialogRef.close({ isDelete: true });
			},
			(error: HttpErrorResponse) => {
				this.errorMessage = error.error;
			}
		);
	}

	public isDateWithinTrip(dateToTest: moment.Moment): boolean {
		let isWithinTrip: boolean = false;

		this.modalData.timesheet.trips.forEach(t => {
			const mStart: moment.Moment = moment.utc(t.tripStart);
			const mEnd: moment.Moment = moment.utc(t.tripEnd);
			if (dateToTest.isBetween(mStart, mEnd, 'minutes', '()')) {
				isWithinTrip = true;
			}
		});

		return isWithinTrip;
	}

	public onTimeChange($event: any, controlName: string): void {
		if ($event.target.placeholder === 'HH') {
			if (!$event.target.value) {
				this.form.get(controlName).patchValue(null);
			}
			this[`${controlName}HoursError`] = !$event.target.value;
		}
		if ($event.target.placeholder === 'MM') {
			if (!$event.target.value) {
				this.form.get(controlName).patchValue(null);
			}
			this[`${controlName}MinutesError`] = !$event.target.value;
		}

		if (controlName === 'startTime') {
			this.combineDateTimeValues('perDiemStart', 'startTime');
			this.startWithinTrip = this.isDateWithinTrip(moment.utc(this.form.value.perDiemStart));
		} else {
			this.combineDateTimeValues('perDiemEnd', 'endTime');
			this.endWithinTrip = this.isDateWithinTrip(moment.utc(this.form.value.perDiemEnd));
		}
	}

	private combineDateTimeValues(dateControl: string, timeControl: string): void {
		if (!this.form.value[dateControl] || !this.form.value[timeControl]) {
			return;
		}

		const tempDate: moment.Moment = moment.utc(this.form.value[dateControl]);
		tempDate.hours(0 + this.form.value[timeControl].split(':')[0]);
		tempDate.minutes(0 + this.form.value[timeControl].split(':')[1]);
		// tempDate.set('hours', this.form.value[timeControl].split(':')[0]);
		// tempDate.set('minutes', this.form.value[timeControl].split(':')[1]);
		this.form.get(dateControl).patchValue(tempDate);
	}
}
